import React, { useEffect, useState } from 'react';
import Sidebar from '@/components/Sidebar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/PasswordInput';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Key, Users, Trash2, UserPlus, AlertTriangle, Edit, Mail, Check } from 'lucide-react';
import api from '@/utils/api';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [familyProfileCount, setFamilyProfileCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [resetDialog, setResetDialog] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [editDialog, setEditDialog] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [resetting, setResetting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [sendWelcomeEmail, setSendWelcomeEmail] = useState(true);
  const [newUser, setNewUser] = useState({
    username: '',
    password: '',
    email: '',
    full_name: '',
    role: 'staff'
  });
  const [editUser, setEditUser] = useState({
    email: '',
    full_name: ''
  });

  useEffect(() => {
    fetchUsers();
    fetchFamilyCount();
    // Get current user info
    const userStr = localStorage.getItem('user');
    if (userStr) {
      setCurrentUser(JSON.parse(userStr));
    }
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/admin/users');
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const fetchFamilyCount = async () => {
    try {
      const response = await api.get('/families');
      setFamilyProfileCount(response.data.length);
    } catch (error) {
      console.error('Failed to fetch family count');
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setCreating(true);
    
    try {
      const userData = {
        username: newUser.username,
        password: newUser.password,
        role: newUser.role
      };
      if (newUser.email && sendWelcomeEmail) {
        userData.email = newUser.email;
      }
      if (newUser.full_name) {
        userData.full_name = newUser.full_name;
      }
      
      const response = await api.post('/admin/create-user', userData);
      
      if (response.data.email_sent) {
        toast.success(`Account created and welcome email sent to ${newUser.email}!`);
      } else if (newUser.email && sendWelcomeEmail) {
        toast.success('Account created but email could not be sent. Please share credentials manually.');
      } else {
        toast.success(`${newUser.role.charAt(0).toUpperCase() + newUser.role.slice(1)} account created successfully!`);
      }
      
      setShowCreateDialog(false);
      setNewUser({ username: '', password: '', email: '', full_name: '', role: 'staff' });
      setSendWelcomeEmail(true);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    } finally {
      setCreating(false);
    }
  };

  const handleEditUser = async () => {
    if (!editDialog) return;
    
    setUpdating(true);
    try {
      await api.put(`/admin/users/${editDialog.id}`, {
        email: editUser.email || null,
        full_name: editUser.full_name || null
      });
      toast.success('User profile updated successfully!');
      setEditDialog(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    } finally {
      setUpdating(false);
    }
  };

  const handleResetPassword = async () => {
    if (!resetDialog || !newPassword) return;
    
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setResetting(true);
    try {
      await api.post(`/auth/reset-password/${resetDialog.id}`, {
        new_password: newPassword
      });
      toast.success('Password reset successfully!');
      setResetDialog(null);
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setResetting(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteDialog) return;
    
    setDeleting(true);
    try {
      await api.delete(`/admin/users/${deleteDialog.id}`);
      toast.success('User deleted successfully!');
      setDeleteDialog(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    } finally {
      setDeleting(false);
    }
  };

  const openEditDialog = (user) => {
    setEditUser({
      email: user.email || '',
      full_name: user.full_name || ''
    });
    setEditDialog(user);
  };

  // Separate users by role for display
  const adminUsers = users.filter(u => u.role === 'admin');
  const staffUsers = users.filter(u => u.role === 'staff');
  const volunteerUsers = users.filter(u => u.role === 'volunteer');

  return (
    <div className="flex">
      <Sidebar />
      <div className="ml-64 flex-1 p-8" data-testid="user-management-page">
        <div className="max-w-6xl mx-auto">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-4xl font-bold text-foreground mb-2">User Management</h1>
              <p className="text-stone-600">Manage admin, staff, and volunteer accounts</p>
            </div>
            <Button
              onClick={() => setShowCreateDialog(true)}
              className="rounded-full"
              data-testid="create-user-button"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Create User Account
            </Button>
          </div>

          {loading ? (
            <div className="text-center py-12">Loading...</div>
          ) : (
            <div className="space-y-6">
              {/* Admin Users */}
              <Card className="border-stone-200 shadow-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Badge className="bg-red-100 text-red-800">Admin</Badge>
                    Administrators ({adminUsers.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {adminUsers.length === 0 ? (
                    <p className="text-stone-500 text-center py-4">No admin users</p>
                  ) : (
                    <div className="space-y-3" data-testid="admin-users-list">
                      {adminUsers.map((user) => (
                        <UserRow 
                          key={user.id} 
                          user={user} 
                          currentUser={currentUser}
                          onEdit={() => openEditDialog(user)}
                          onResetPassword={() => {
                            setResetDialog(user);
                            setNewPassword('');
                          }}
                          onDelete={() => setDeleteDialog(user)}
                        />
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Staff Users */}
              <Card className="border-stone-200 shadow-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Badge className="bg-blue-100 text-blue-800">Staff</Badge>
                    Staff Members ({staffUsers.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {staffUsers.length === 0 ? (
                    <p className="text-stone-500 text-center py-4">No staff users</p>
                  ) : (
                    <div className="space-y-3" data-testid="staff-users-list">
                      {staffUsers.map((user) => (
                        <UserRow 
                          key={user.id} 
                          user={user}
                          currentUser={currentUser}
                          onEdit={() => openEditDialog(user)}
                          onResetPassword={() => {
                            setResetDialog(user);
                            setNewPassword('');
                          }}
                          onDelete={() => setDeleteDialog(user)}
                        />
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Volunteer Users */}
              <Card className="border-stone-200 shadow-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Badge className="bg-purple-100 text-purple-800">Volunteer</Badge>
                    Volunteers ({volunteerUsers.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {volunteerUsers.length === 0 ? (
                    <p className="text-stone-500 text-center py-4">No volunteer users</p>
                  ) : (
                    <div className="space-y-3" data-testid="volunteer-users-list">
                      {volunteerUsers.map((user) => (
                        <UserRow 
                          key={user.id} 
                          user={user}
                          currentUser={currentUser}
                          onEdit={() => openEditDialog(user)}
                          onResetPassword={() => {
                            setResetDialog(user);
                            setNewPassword('');
                          }}
                          onDelete={() => setDeleteDialog(user)}
                        />
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Family Users (read-only summary) */}
              <Card className="border-stone-200 shadow-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Badge className="bg-green-100 text-green-800">Family</Badge>
                    Registered Families ({familyProfileCount})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-stone-500 text-sm">
                    Families register and complete their profiles through the Family Portal. Manage family data in the Families section.
                  </p>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>

      {/* Create User Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent data-testid="create-user-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Admin/Staff Account</DialogTitle>
            <DialogDescription>
              Create a new administrator or staff account with system access
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateUser}>
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="new-fullname">Full Name</Label>
                <Input
                  id="new-fullname"
                  data-testid="new-fullname-input"
                  value={newUser.full_name}
                  onChange={(e) => setNewUser({ ...newUser, full_name: e.target.value })}
                  className="h-11"
                  placeholder="Enter full name"
                />
              </div>
              <div>
                <Label htmlFor="new-username">Username *</Label>
                <Input
                  id="new-username"
                  data-testid="new-username-input"
                  value={newUser.username}
                  onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                  required
                  className="h-11"
                  placeholder="Enter username"
                />
              </div>
              <div>
                <Label htmlFor="new-email">Email</Label>
                <Input
                  id="new-email"
                  data-testid="new-email-input"
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  className="h-11"
                  placeholder="Enter email address"
                />
                {newUser.email && (
                  <div className="flex items-center gap-2 mt-2">
                    <Checkbox
                      id="send-welcome-email"
                      checked={sendWelcomeEmail}
                      onCheckedChange={(checked) => setSendWelcomeEmail(checked)}
                    />
                    <label htmlFor="send-welcome-email" className="text-sm text-stone-600 cursor-pointer">
                      Send welcome email with login credentials
                    </label>
                  </div>
                )}
              </div>
              <div>
                <Label htmlFor="new-password">Password *</Label>
                <PasswordInput
                  id="new-password"
                  data-testid="new-password-input"
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  required
                  minLength={6}
                  className="h-11"
                  placeholder="Enter password (min 6 characters)"
                />
              </div>
              <div>
                <Label htmlFor="new-role">Role *</Label>
                <select
                  id="new-role"
                  data-testid="new-role-select"
                  value={newUser.role}
                  onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                  className="w-full h-11 px-4 rounded-lg border border-stone-200 focus:border-primary focus:ring-2 focus:ring-primary/20"
                  required
                >
                  <option value="volunteer">Volunteer - Add families, confirm deliveries, update inventory</option>
                  <option value="staff">Staff (Level 2) - Full access except delete families & reset passwords</option>
                  <option value="admin">Admin - Full system access</option>
                </select>
                <p className="text-xs text-stone-500 mt-1">
                  {newUser.role === 'admin' 
                    ? 'Full system access: Can create users, delete families, reset passwords, and access all features' 
                    : newUser.role === 'staff'
                    ? 'Level 2 access: Can manage families and deliveries, but cannot delete families or reset passwords'
                    : 'Limited access: Can add families, confirm deliveries, and update inventory only'}
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowCreateDialog(false)}
                data-testid="create-cancel"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="rounded-full"
                disabled={creating}
                data-testid="create-submit"
              >
                {creating ? 'Creating...' : 'Create Account'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={!!editDialog} onOpenChange={(open) => !open && setEditDialog(null)}>
        <DialogContent data-testid="edit-user-dialog">
          <DialogHeader>
            <DialogTitle>Edit User Profile</DialogTitle>
            <DialogDescription>
              Update profile for <strong>{editDialog?.username}</strong>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="edit-fullname">Full Name</Label>
              <Input
                id="edit-fullname"
                data-testid="edit-fullname-input"
                value={editUser.full_name}
                onChange={(e) => setEditUser({ ...editUser, full_name: e.target.value })}
                className="h-11"
                placeholder="Enter full name"
              />
            </div>
            <div>
              <Label htmlFor="edit-email">Email</Label>
              <Input
                id="edit-email"
                data-testid="edit-email-input"
                type="email"
                value={editUser.email}
                onChange={(e) => setEditUser({ ...editUser, email: e.target.value })}
                className="h-11"
                placeholder="Enter email address"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditDialog(null)}
              data-testid="edit-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={handleEditUser}
              disabled={updating}
              className="rounded-full"
              data-testid="edit-submit"
            >
              {updating ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={!!resetDialog} onOpenChange={(open) => !open && setResetDialog(null)}>
        <DialogContent data-testid="reset-password-dialog">
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
            <DialogDescription>
              Enter a new password for <strong>{resetDialog?.username}</strong>
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="new_password">New Password</Label>
            <PasswordInput
              id="new_password"
              data-testid="reset-password-input"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter new password"
              minLength={6}
              className="h-11"
            />
            <p className="text-xs text-stone-500 mt-1">Must be at least 6 characters</p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setResetDialog(null)}
              data-testid="reset-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={handleResetPassword}
              disabled={resetting || !newPassword}
              className="rounded-full"
              data-testid="reset-confirm"
            >
              {resetting ? 'Resetting...' : 'Reset Password'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete User Confirmation Dialog */}
      <Dialog open={!!deleteDialog} onOpenChange={(open) => !open && setDeleteDialog(null)}>
        <DialogContent data-testid="delete-user-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Delete User Account
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete the account <strong>{deleteDialog?.username}</strong>? 
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialog(null)}
              data-testid="delete-cancel"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteUser}
              disabled={deleting}
              data-testid="delete-confirm"
            >
              {deleting ? 'Deleting...' : 'Delete Account'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// UserRow component for displaying individual users
function UserRow({ user, currentUser, onEdit, onResetPassword, onDelete }) {
  const isCurrentUser = currentUser?.id === user.id;
  
  return (
    <div
      className="p-4 border border-stone-200 rounded-lg flex justify-between items-center hover:bg-stone-50 transition-colors"
      data-testid={`user-${user.id}`}
    >
      <div>
        <div className="flex items-center gap-2">
          {user.full_name ? (
            <>
              <p className="font-medium text-lg">{user.full_name}</p>
              <span className="text-stone-500">({user.username})</span>
            </>
          ) : (
            <p className="font-medium text-lg">{user.username}</p>
          )}
          {isCurrentUser && (
            <Badge variant="outline" className="text-xs">You</Badge>
          )}
        </div>
        <div className="flex gap-3 text-sm text-stone-600 mt-1">
          {user.email && (
            <span className="flex items-center gap-1">
              <Mail className="w-3 h-3" />
              {user.email}
            </span>
          )}
          <span>Created: {new Date(user.created_at).toLocaleDateString()}</span>
        </div>
      </div>
      <div className="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={onEdit}
          data-testid={`edit-user-${user.id}`}
        >
          <Edit className="w-4 h-4 mr-2" />
          Edit
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={onResetPassword}
          data-testid={`reset-password-${user.id}`}
        >
          <Key className="w-4 h-4 mr-2" />
          Reset Password
        </Button>
        {!isCurrentUser && (
          <Button
            variant="outline"
            size="sm"
            onClick={onDelete}
            className="text-red-600 hover:text-red-700 hover:bg-red-50"
            data-testid={`delete-user-${user.id}`}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        )}
      </div>
    </div>
  );
}
